import { Injectable, computed, signal } from '@angular/core';

import { ArcEndpoints } from '@core/utils/geo-polyline.utils';

import { ToolboxItem } from '@shared/components/toolbox/toolbox.component';

import { GeoLocation } from '@features/components/visualization/mapbox/models/map-data';

/** Identity for an arc being created, which has no uid until it is first saved. */
export const NEW_ARC_UID = '__new_geo_arc__';

export type GeoArcEditNotice = 'provider-failed' | 'too-many-vias';

/**
 * Vias travel in the provider request's URL path. Past this many the URL exceeds what GBE
 * accepts and the request fails opaquely, so the cap is enforced here and reported.
 */
export const MAX_VIAS = 20;

export interface GeoArcEditRequest {
  uid: string;
  label?: string;
  path: GeoLocation[];
  endpoints: ArcEndpoints | null;
  compute: (points: GeoLocation[]) => Promise<GeoLocation[]>;
  onCommit?: (path: GeoLocation[]) => void;
}

/**
 * The map renders arcs from the visualization store, but the store is fed from the form,
 * which is fed from the map while a session runs. Refusing inbound data for the arc under
 * edit breaks that cycle, so a drag is never overwritten mid-gesture.
 */
export function shouldApplyIncomingRoute(
  routeUid: string,
  activeUid: string | null
): boolean {
  return routeUid !== activeUid;
}

@Injectable({ providedIn: 'root' })
export class GeoArcEditSessionService {
  private readonly activeUidState = signal<string | null>(null);
  /** Null means the arc being edited has no name yet, e.g. one still being created. */
  private readonly labelState = signal<string | null>(null);
  private readonly pathState = signal<GeoLocation[]>([]);
  private readonly viasState = signal<GeoLocation[]>([]);
  private readonly endpointsState = signal<ArcEndpoints | null>(null);
  private readonly isComputingState = signal(false);
  private readonly noticeState = signal<GeoArcEditNotice | null>(null);
  private readonly toolboxActionState = signal<ToolboxItem | null>(null);

  private request: GeoArcEditRequest | null = null;

  readonly activeUid = this.activeUidState.asReadonly();
  readonly label = this.labelState.asReadonly();
  readonly path = this.pathState.asReadonly();
  readonly vias = this.viasState.asReadonly();
  readonly endpoints = this.endpointsState.asReadonly();
  readonly isComputing = this.isComputingState.asReadonly();
  readonly notice = this.noticeState.asReadonly();
  readonly toolboxAction = this.toolboxActionState.asReadonly();
  readonly isActive = computed(() => this.activeUidState() !== null);

  registerToolboxAction(action: ToolboxItem | null): void {
    this.toolboxActionState.set(action);
  }

  begin(request: GeoArcEditRequest): void {
    this.request = request;
    this.activeUidState.set(request.uid);
    this.labelState.set(request.label ?? null);
    this.pathState.set(request.path);
    this.endpointsState.set(request.endpoints);
    this.viasState.set([]);
    this.noticeState.set(null);
  }

  async insertVia(via: GeoLocation, index: number): Promise<void> {
    const next = [...this.viasState()];
    next.splice(index, 0, via);
    await this.applyVias(next);
  }

  async moveVia(index: number, via: GeoLocation): Promise<void> {
    const next = [...this.viasState()];
    next[index] = via;
    await this.applyVias(next);
  }

  async removeVia(index: number): Promise<void> {
    const next = this.viasState().filter((_, position) => position !== index);
    await this.applyVias(next);
  }

  /**
   * Recomputes through the current vias. A refusal leaves the previous path and vias
   * standing: discarding an authored geometry because a network call failed is the failure
   * mode this whole feature is built to avoid.
   */
  private async applyVias(vias: GeoLocation[]): Promise<void> {
    const request = this.request;
    const endpoints = this.endpointsState();
    if (!request || !endpoints || this.isComputingState()) {
      return;
    }
    if (vias.length > MAX_VIAS) {
      this.noticeState.set('too-many-vias');
      return;
    }
    this.isComputingState.set(true);
    this.noticeState.set(null);
    try {
      const path = await request.compute([endpoints.tail, ...vias, endpoints.head]);
      // The session can have ended, or been replaced by another arc's, while the request
      // was in flight. Its result belongs to the session that asked for it and to no other.
      if (this.request !== request) {
        return;
      }
      if (path.length === 0) {
        this.noticeState.set('provider-failed');
        return;
      }
      this.viasState.set(vias);
      this.pathState.set(path);
    } catch {
      if (this.request === request) {
        this.noticeState.set('provider-failed');
      }
    } finally {
      // end() already cleared the flag for an abandoned request; clearing it again here
      // would release a lock the session that replaced this one is holding.
      if (this.request === request) {
        this.isComputingState.set(false);
      }
    }
  }

  async recompute(): Promise<void> {
    await this.applyVias(this.viasState());
  }

  clear(): void {
    this.pathState.set([]);
    this.viasState.set([]);
    this.noticeState.set(null);
  }

  commit(): void {
    // end() first, mirroring GeoAreaEditSessionService: the callback typically writes a
    // form value that re-renders the map, and it must not run against a live session.
    const onCommit = this.request?.onCommit;
    const path = this.pathState();
    this.end();
    onCommit?.(path);
  }

  /**
   * Discard needs nothing restored: the session is the only holder of the path it is
   * building, and nothing is written back to the form until commit().
   */
  cancel(): void {
    this.end();
  }

  end(): void {
    this.request = null;
    this.activeUidState.set(null);
    this.labelState.set(null);
    this.pathState.set([]);
    this.viasState.set([]);
    this.endpointsState.set(null);
    this.noticeState.set(null);
    // A session ended mid-request leaves nothing waiting on that request. Keeping the flag
    // set would make applyVias refuse the next session's very first compute.
    this.isComputingState.set(false);
  }
}
